import React from 'react';
import { Dialog, Button, Typography, Box, IconButton, keyframes } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface PointsCheckDialogProps {
  open: boolean;
  onClose: () => void;
  isZeroPoints?: boolean;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const PointsCheckDialog: React.FC<PointsCheckDialogProps> = ({
  open,
  onClose,
  isZeroPoints = false,
}) => {
  const navigate = useNavigate();

  // 添加调试日志
  React.useEffect(() => {
    console.log('[PointsCheckDialog] 状态变化:', { open, isZeroPoints });
  }, [open, isZeroPoints]);

  const handleGoToRecharge = () => {
    onClose();
    navigate('/profile');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '12px',
          bgcolor: '#ffffff',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
          overflow: 'visible',
          maxWidth: 380,
          width: '100%',
          animation: `${fadeIn} 0.25s ease-out`,
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(0, 0, 0, 0.25)',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          bgcolor: '#ffffff',
          p: 4.5,
          borderRadius: '12px',
        }}
      >
        {/* 关闭按钮 */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            width: 32,
            height: 32,
            color: '#94a3b8',
            '&:hover': {
              bgcolor: '#f8fafc',
              color: '#64748b',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>

        {/* 主内容区 */}
        <Box sx={{ pt: 1 }}>
          {/* 轻量化线性图标 */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2.5,
            }}
          >
            <ErrorOutlineIcon
              sx={{
                fontSize: 48,
                color: isZeroPoints ? '#dc2626' : '#2563eb',
                strokeWidth: 0.5,
              }}
            />
          </Box>

          {/* 标题 */}
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 700,
              textAlign: 'center',
              color: '#0f172a',
              mb: 1,
              fontFamily: 'PingFang SC, Inter, -apple-system, sans-serif',
              letterSpacing: '-0.02em',
            }}
          >
            {isZeroPoints ? '积分已用完' : '积分不足'}
          </Typography>

          {/* 副标题 */}
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 400,
              textAlign: 'center',
              color: '#64748b',
              mb: 4,
              fontFamily: 'PingFang SC, Inter, -apple-system, sans-serif',
            }}
          >
            {isZeroPoints ? '您的账户余额不足以继续使用' : '当前操作需要更多积分'}
          </Typography>

          {/* 按钮组 - 分级设计 */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            {/* Ghost Button - 取消按钮 */}
            <Button
              onClick={onClose}
              sx={{
                py: 1,
                px: 4.5,
                borderRadius: '10px',
                fontWeight: 500,
                fontSize: 15,
                textTransform: 'none',
                color: '#64748b',
                bgcolor: 'transparent',
                border: '1px solid #e2e8f0',
                fontFamily: 'PingFang SC, Inter, -apple-system, sans-serif',
                minWidth: 120,
                '&:hover': {
                  bgcolor: '#f8fafc',
                  border: '1px solid #cbd5e1',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {isZeroPoints ? '关闭' : '取消'}
            </Button>

            {/* 实色填充按钮 - 去充值 */}
            <Button
              onClick={handleGoToRecharge}
              sx={{
                py: 1,
                px: 4.5,
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: 15,
                textTransform: 'none',
                color: '#ffffff',
                bgcolor: isZeroPoints ? '#dc2626' : '#2563eb',
                fontFamily: 'PingFang SC, Inter, -apple-system, sans-serif',
                boxShadow: 'none',
                minWidth: 120,
                '&:hover': {
                  bgcolor: isZeroPoints ? '#b91c1c' : '#1d4ed8',
                  boxShadow: 'none',
                },
                transition: 'all 0.2s ease',
              }}
            >
              去充值
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default PointsCheckDialog;
